import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Box, Button, IconButton, MenuItem, TextField, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { bareName, detectPackages } from 'new/components/flows/types/byo/v2/packageDetect';
import { hasUnsafeExtrasComma } from 'new/components/flows/types/byo/v2/packages';
import { useFeatureFlag } from 'new/hooks/useFeatureFlags';
import classes from './studio.module.scss';

type Constraint = '==' | '>=' | 'any';
interface PkgRow {
  name: string;
  constraint: Constraint;
  version: string;
  /** A requirement the row can't represent (`~=`, `<`, extras…) — saved verbatim until edited. */
  raw?: string;
}

export interface PackagesTableProps {
  /** Canonical package list (`name==version` | `name>=version` | `name`). */
  value: string[];
  onChange: (next: string[]) => void;
  /** Python source — scanned to auto-detect imported packages. */
  code: string;
  /**
   * Optional externally-owned memory for "names the user removed" and
   * "has the user touched the list" — pass a ref owned by a PARENT that
   * doesn't unmount (e.g. StudioCodeStep) when this table lives inside a
   * MUI Popover. Popovers unmount their content on close by default, so a
   * purely-local ref here forgets everything every time the user closes the
   * popover (e.g. by clicking back into the code editor) and reopens it —
   * confirmed live 2026-08-20: that reopen is a fresh mount, which is why
   * dismissed auto-detected packages could resurrect. Falls back to an
   * internal ref when omitted (existing tests, any other caller).
   */
  dismissedRef?: React.MutableRefObject<Set<string>>;
  userEditedRef?: React.MutableRefObject<boolean>;
  'data-testid'?: string;
}

/** Parse a requirement string into a row. */
function toRow(req: string): PkgRow {
  const m = req.match(/^([A-Za-z0-9._-]+)\s*(==|>=)\s*(.*)$/);
  if (m) return { name: m[1], constraint: m[2] as Constraint, version: m[3].trim() };
  const name = bareName(req);
  return {
    name,
    constraint: 'any',
    version: '',
    raw: req.trim() !== name ? req.trim() : undefined,
  };
}

/** Serialize a row back to a requirement string (empty if no name). */
function toReq(row: PkgRow): string {
  const n = row.name.trim();
  if (!n) return '';
  if (row.raw) return row.raw;
  if (row.constraint === 'any') return n;
  return `${n}${row.constraint}${row.version.trim()}`;
}

const rowValid = (r: PkgRow) => !r.name.trim() || r.constraint === 'any' || !!r.version.trim();

/**
 * False for a pinned requirement with no version (`pandas==`). Callers gate Save
 * on the package LIST rather than on this table reporting it: the table lives in
 * a Popover that unmounts when closed, so it never sees a list replaced while
 * closed (e.g. Load as Template).
 */
export const isPackageReqValid = (req: string) => rowValid(toRow(req));

/** Order-sensitive content equality for two requirement-string arrays. */
function sameReqs(a: string[], b: string[]): boolean {
  return a.length === b.length && a.every((r, i) => r === b[i]);
}

/**
 * Packages editor (KEY / CONSTRAINT / VERSION) backed by the existing
 * `detectPackages` import scanner and the canonical `name==version` list.
 * Auto-seeds packages imported by the code; a pinned row (== / >=) with no
 * version is invalid and blocks Continue.
 */
export function PackagesTable({
  value,
  onChange,
  code,
  dismissedRef: dismissedRefProp,
  userEditedRef: userEditedRefProp,
  'data-testid': dataTestId = 'byo-packages-table',
}: PackagesTableProps) {
  // No placeholder row by default — an empty list just shows the empty table
  // (headers + "+ Add package"). `value` (the code container's decoded
  // packages) can arrive asynchronously after this table's first mount, so
  // resync from it whenever it changes, but only while the user hasn't
  // touched the list yet (added/removed/edited a row locally).
  const [rows, setRows] = useState<PkgRow[]>(() => (value ?? []).map(toRow).filter((r) => r.name));
  // Tracks a genuine user edit (add/remove/update a row) — NOT whether `rows`
  // happens to be non-empty. Using `prev.length > 0` as that guard used to
  // race the real hydration: if code (and therefore auto-detect) is available
  // before the saved `value` loads, auto-detect populates `rows` first, the
  // guard then reads "already touched" for a reason that has nothing to do
  // with the user, and the real saved packages that arrive afterward are
  // silently dropped forever. Confirmed 2026-08-19.
  const internalUserEditedRef = useRef(false);
  const userEditedRef = userEditedRefProp ?? internalUserEditedRef;
  // Requirement-string snapshots we've pushed up via the propagate effect
  // below but haven't yet seen echoed back down as `value` — a FIFO queue,
  // not a single slot. `onChange` always reports THIS render's `rows`, but
  // the parent takes a full round trip to echo that back down as a new
  // `value` prop, and `rows` can move on locally (e.g. auto-detect adding a
  // package right after mount) before that round trip completes — so
  // multiple echoes can be in flight at once. A single "last echoed" slot
  // gets overwritten by each new local change before the OLDEST echo's
  // round trip lands, so by the time it arrives it no longer matches
  // "last echoed" and gets mistaken for a genuinely new (stale) external
  // value — wiping out the newer local state and repeating forever.
  // Confirmed live 2026-08-21 on a brand-new source (empty saved `value`,
  // code already importing a package): `value` oscillated
  // `[] <-> ["pandas"]` forever, CPU pegged, chasing a one-generation-stale
  // echo a single slot couldn't remember past the next local change. A
  // queue matches each incoming `value` against every echo still in
  // flight, not just the most recent one, so it correctly recognizes ANY
  // of our own pending echoes regardless of how many piled up first — while
  // a value that matches none of them (a genuinely new external update,
  // e.g. the container's real saved packages finally arriving) still gets
  // applied, preserving the 2026-08-19 fix for that case.
  const pendingEchoesRef = useRef<string[][]>([]);
  useEffect(() => {
    setRows((prev) => {
      if (userEditedRef.current) return prev;
      const nextReqs = (value ?? []).filter(Boolean);
      const echoIdx = pendingEchoesRef.current.findIndex((echo) => sameReqs(echo, nextReqs));
      if (echoIdx !== -1) {
        // Our own echo catching up — drop it and everything queued before
        // it (now presumed superseded too), and don't resync from it.
        pendingEchoesRef.current.splice(0, echoIdx + 1);
        return prev;
      }
      const prevReqs = prev.map(toReq).filter(Boolean);
      if (sameReqs(nextReqs, prevReqs)) return prev;
      return nextReqs.map(toRow).filter((r) => r.name);
    });
  }, [value, userEditedRef]);

  // Auto-detect imported packages and append any not already present. Names
  // the user has explicitly removed are remembered here so a LATER, unrelated
  // code edit (which recomputes `detected` since it depends on `code`) can't
  // silently resurrect a package the user just deleted — this effect used to
  // re-add any still-imported name on every code change regardless of intent.
  // Confirmed 2026-08-19.
  //
  // Gated by UI_BYO_AUTO_PACKAGE_DETECTION (default false, requested in
  // 2026-08-20 product review): auto-adding a package the moment its import
  // appears is the UI silently forming an opinion and modifying an existing
  // pipeline's package list — needs a per-org opt-in before it applies to
  // anyone's real pipelines, not blanket-on for everyone by default.
  const autoDetectEnabled = useFeatureFlag('UI_BYO_AUTO_PACKAGE_DETECTION');
  const internalDismissedRef = useRef<Set<string>>(new Set());
  const dismissedRef = dismissedRefProp ?? internalDismissedRef;
  const detected = useMemo(() => detectPackages(code), [code]);
  useEffect(() => {
    if (!autoDetectEnabled) {
      return;
    }
    setRows((prev) => {
      const present = new Set(prev.map((r) => r.name.toLowerCase()));
      const additions = detected
        .filter((d) => !present.has(d.toLowerCase()) && !dismissedRef.current.has(d.toLowerCase()))
        .map<PkgRow>((d) => ({ name: d, constraint: 'any', version: '' }));
      if (!additions.length) return prev;
      return [...prev, ...additions];
    });
  }, [detected, autoDetectEnabled, dismissedRef]);

  // Propagate the serialized value whenever rows change.
  useEffect(() => {
    const reqs = rows.map(toReq).filter(Boolean);
    pendingEchoesRef.current.push(reqs);
    // Backstop only — in practice this drains within a render or two.
    if (pendingEchoesRef.current.length > 20) pendingEchoesRef.current.shift();
    onChange(reqs);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows]);

  const update = (i: number, patch: Partial<PkgRow>) => {
    userEditedRef.current = true;
    setRows((prev) => prev.map((r, idx) => (idx === i ? { ...r, ...patch, raw: undefined } : r)));
  };
  const remove = (i: number) => {
    userEditedRef.current = true;
    setRows((prev) => {
      const removedName = prev[i]?.name?.trim().toLowerCase();
      if (removedName) dismissedRef.current.add(removedName);
      return prev.filter((_, idx) => idx !== i);
    });
  };
  const add = () => {
    userEditedRef.current = true;
    setRows((prev) => [...prev, { name: '', constraint: 'any', version: '' }]);
  };

  const hasAny = rows.some((r) => r.name.trim() && r.constraint === 'any' && !r.raw);
  // hasUnsafeExtrasComma existed since the packages-runtime fix but had zero
  // call sites — the one case it exists to catch (a comma inside pip extras,
  // e.g. `requests[security,socks]`) shipped with no warning at all, even
  // though the backend's non-bracket-aware comma split silently mis-parses
  // it. Confirmed bug, fixed 2026-08-20.
  const hasUnsafeExtras = rows.some((r) => hasUnsafeExtrasComma(r.name));

  return (
    <Box data-testid={dataTestId}>
      <Typography className={classes.sectionTitle}>Python packages</Typography>
      <Typography className={classes.sectionSubtitle}>
        Declared packages are pip-installed into the runtime before your code runs. Pin a version
        for reproducible jobs.
      </Typography>

      <Box className={classes.pkgTable}>
        <Box className={classes.pkgHeadRow}>
          <span>Package (key)</span>
          <span>Constraint (type)</span>
          <span>Version (value)</span>
          <span />
        </Box>
        {rows.map((row, i) => {
          const invalid = !rowValid(row);
          return (
            <Box className={classes.pkgRow} key={i}>
              <TextField
                size="small"
                placeholder="package"
                value={row.name}
                onChange={(e) => update(i, { name: e.target.value })}
                inputProps={{ 'data-testid': `${dataTestId}-name-${i}` }}
              />
              <TextField
                size="small"
                select
                value={row.constraint}
                onChange={(e) => update(i, { constraint: e.target.value as Constraint })}
              >
                <MenuItem value="==">{'='}</MenuItem>
                <MenuItem value=">=">{'≥'}</MenuItem>
                <MenuItem value="any">any</MenuItem>
              </TextField>
              <TextField
                size="small"
                placeholder={
                  row.raw?.slice(row.name.length) || (row.constraint === 'any' ? '—' : 'x.y.z')
                }
                value={row.version}
                disabled={row.constraint === 'any'}
                error={invalid}
                onChange={(e) => update(i, { version: e.target.value })}
                inputProps={{ 'data-testid': `${dataTestId}-version-${i}` }}
              />
              <IconButton size="small" onClick={() => remove(i)} aria-label="Remove package">
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>
          );
        })}
        <Button onClick={add} className={classes.pkgAdd} data-testid={`${dataTestId}-add`}>
          + Add package
        </Button>
      </Box>

      {hasAny ? (
        <Typography className={classes.pkgWarn}>
          ⚠ Packages with constraint &quot;any&quot; install the latest version and aren&apos;t
          reproducible across runs.
        </Typography>
      ) : null}
      {hasUnsafeExtras ? (
        <Typography className={classes.pkgWarn} data-testid={`${dataTestId}-extras-warning`}>
          ⚠ A comma inside brackets (e.g. pkg[extra1,extra2]) isn&apos;t supported — the runtime
          splits packages on every comma and will misread it. Use a single extra, or list separate
          rows.
        </Typography>
      ) : null}
    </Box>
  );
}

export default PackagesTable;
